/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'
import { m, type Transition } from 'motion/react'
import s from './expanding-glow.module.css'

const WIDTH = 1600
const HEIGHT = 900
const SCALE = 6
const DURATION = 15
const REPEAT_DELAY = 3
const RECTANGLE_CONFIG = {
	initial: {
		opacity: 0,
		scale: 2,
		y: 0,
	},
}
const TRANSITION_CONFIG: Transition = {
	duration: DURATION,
	repeatDelay: 5,
	ease: 'easeIn',
	repeat: Infinity,
	opacity: {
		times: [0, 0.05, 0.95, 1],
		duration: DURATION,
		ease: 'linear',
		repeat: Infinity,
		repeatDelay: REPEAT_DELAY,
	},
	scale: {
		duration: DURATION,
		ease: 'easeIn',
		repeat: Infinity,
		repeatDelay: REPEAT_DELAY,
	},
	y: {
		duration: DURATION,
		ease: 'easeIn',
		repeat: Infinity,
		repeatDelay: REPEAT_DELAY,
	},
}
const RECTS = [
	{
		x: 815,
		y: HEIGHT * 0.25,
		width: 482,
		height: 150,
		rx: 96,
		fill: 'url(#pink)',
		direction: 'UP',
		mixBlendMode: 'lighten',
	},
	{
		x: 720,
		y: HEIGHT * 0.24,
		width: 380,
		height: 124,
		rx: 62,
		fill: 'url(#yellow)',
		direction: 'UP',
	},
	{
		x: 328,
		y: HEIGHT * 0.21,
		width: 444,
		height: 147,
		rx: 73,
		fill: 'url(#purple)',
		direction: 'UP',
	},
	{
		x: 476,
		y: HEIGHT * 0.24,
		width: 359,
		height: 146,
		rx: 73,
		fill: 'url(#teal)',
		direction: 'UP',
	},
	{
		x: 350,
		// We need to subtract the height since the Y value
		// doesn't take height into account
		y: HEIGHT * 0.75 - 150,
		width: 482,
		height: 150,
		rx: 96,
		fill: 'url(#pink)',
		direction: 'DOWN',
		mixBlendMode: 'lighten',
	},
	{
		x: 500,
		// We need to subtract the height since the Y value
		// doesn't take height into account
		y: HEIGHT * 0.75 - 124,
		width: 380,
		height: 124,
		rx: 62,
		fill: 'url(#yellow)',
		direction: 'DOWN',
	},
	{
		x: 900,
		// We need to subtract the height since the Y value
		// doesn't take height into account
		y: HEIGHT * 0.79 - 147,
		width: 444,
		height: 147,
		rx: 73,
		fill: 'url(#purple)',
		direction: 'DOWN',
	},
	{
		x: 800,
		// We need to subtract the height since the Y value
		// doesn't take height into account
		y: HEIGHT * 0.76 - 146,
		width: 359,
		height: 146,
		rx: 73,
		fill: 'url(#darkBlue)',
		direction: 'DOWN',
	},
]

export const ExpandingGlow = () => {
	return (
		<svg
			className={s.root}
			viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
			fill="none"
			xmlns="http: www.w3.org/2000/svg"
		>
			<g>
				{RECTS.map((rect, id) => {
					const y = rect.direction === 'UP' ? -1 * HEIGHT : HEIGHT
					const animateProps = {
						opacity: [0, 1, 1, 0],
						scale: SCALE,
						y,
					}

					const style = rect.mixBlendMode
						? {
								['mix-blend-mode' as string]: rect.mixBlendMode,
							}
						: null

					return (
						<g key={id} style={style}>
							<m.rect
								className={s.rectangle}
								{...rect}
								{...RECTANGLE_CONFIG}
								animate={animateProps}
								transition={{
									...TRANSITION_CONFIG,
								}}
							/>
						</g>
					)
				})}
			</g>
			<defs>
				<radialGradient
					id="purple"
					cx="50%"
					cy="50%"
					fx="50%"
					fy="50%"
					r="40%"
					gradientUnits="objectBoundingBox"
				>
					<stop stopColor="#8B78FF" />
					<stop offset="0.3" stopColor="#8B78FF" />
					<stop offset="1" stopColor="#6349FF" stopOpacity="0" />
				</radialGradient>
				<radialGradient
					id="teal"
					cx="50%"
					cy="50%"
					fx="50%"
					fy="50%"
					r="40%"
					gradientUnits="objectBoundingBox"
				>
					<stop stopColor="#5AE9FD" />
					<stop offset="1" stopColor="#5AE9FD" stopOpacity="0" />
				</radialGradient>
				<radialGradient
					id="pink"
					cx="50%"
					cy="50%"
					fx="50%"
					fy="50%"
					r="40%"
					gradientUnits="objectBoundingBox"
				>
					<stop stopColor="#FF47FF" />
					<stop offset="1" stopColor="#FF47FF" stopOpacity="0" />
				</radialGradient>
				<radialGradient
					id="yellow"
					cx="50%"
					cy="50%"
					fx="50%"
					fy="50%"
					r="40%"
					gradientUnits="objectBoundingBox"
				>
					<stop stopColor="#FFE072" />
					<stop offset="1" stopColor="#FFD84B" stopOpacity="0" />
				</radialGradient>
				<radialGradient
					id="darkBlue"
					cx="50%"
					cy="50%"
					fx="50%"
					fy="50%"
					r="40%"
					gradientUnits="objectBoundingBox"
				>
					<stop stopColor="#2737FF" />
					<stop offset="1" stopColor="#2737FF" stopOpacity="0" />
				</radialGradient>
			</defs>
		</svg>
	)
}
